import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/stages";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Btn, CellMain, CellSub, Empty, Panel, Tb, Td, Th } from "@/components/portal/crm/kit";

const num = { fontVariantNumeric: "tabular-nums" };
const BANDS = ["#0055FF", "#2DD4BF", "#7DA9FF", "#0B6B4F", "#1E3A8A", "#5B86D6", "#94A3B8"];
const TOP = 6;

const Legend = ({ series }) => (
  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }} data-testid="override-chart-legend">
    {series.map((s) => (
      <span key={s.key} style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5 }}
            data-testid={`override-legend-${s.key}`}>
        <i style={{ width: 9, height: 9, borderRadius: 3, background: s.colour, display: "inline-block" }} />
        <span style={{ color: "var(--ink-3)", fontWeight: 600 }}>{s.name}</span>
        <span style={{ color: "var(--mut)", ...num }}>{gbp(s.total, { compact: true })}</span>
      </span>
    ))}
  </div>
);

function Hover({ active, payload, label, series }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value > 0).reverse();
  const total = rows.reduce((a, p) => a + p.value, 0);
  const full = payload[0]?.payload?.long || label;
  return (
    <div style={{ background: "#0B1220", color: "#fff", borderRadius: 12, padding: "12px 14px",
                  boxShadow: "0 18px 40px rgba(11,18,32,.3)", minWidth: 190 }}>
      <div style={{ fontSize: 10.5, letterSpacing: ".16em", textTransform: "uppercase", color: "#7da9ff", fontWeight: 700 }}>{full}</div>
      <div style={{ fontSize: 20, fontWeight: 800, margin: "6px 0 8px", ...num }}>{gbp(total)}</div>
      {rows.length === 0 ? <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)" }}>Nothing funded that month</div> : rows.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12.5, padding: "2px 0" }}>
          <i style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: "inline-block", flexShrink: 0 }} />
          <span style={{ color: "rgba(255,255,255,.75)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {series.find((s) => s.key === p.dataKey)?.name || p.dataKey}
          </span>
          <span style={{ fontWeight: 700, ...num }}>{gbp(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

/** Twelve months of override, stacked by the partner who earned it. */
export function RecruitOverrideChart() {
  const [d, setD] = useState(null);
  const [window_, setWindow] = useState(12);

  useEffect(() => {
    let live = true;
    api.get(`/refer/network/overrides?months=${window_}`)
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) setD({ months: [], recruits: [] }); });
    return () => { live = false; };
  }, [window_]);

  const { series, data } = useMemo(() => {
    const recruits = d?.recruits || [];
    const head = recruits.slice(0, TOP);
    const tail = recruits.slice(TOP);
    const s = head.map((r, i) => ({ key: `r${i}`, name: r.name || "A partner", total: r.total_gbp, colour: BANDS[i % BANDS.length], by: r.by_month }));
    if (tail.length) {
      s.push({
        key: "rest", name: `${tail.length} other partner${tail.length === 1 ? "" : "s"}`,
        total: tail.reduce((a, r) => a + r.total_gbp, 0), colour: BANDS[BANDS.length - 1],
        by: (d.months || []).map((_, i) => tail.reduce((a, r) => a + (r.by_month?.[i] || 0), 0)),
      });
    }
    const rows = (d?.months || []).map((m, i) => ({
      label: m.label, long: m.long, year: m.year, total: m.total_gbp,
      ...Object.fromEntries(s.map((x) => [x.key, Number((x.by?.[i] || 0).toFixed(2))])),
    }));
    return { series: s, data: rows };
  }, [d]);

  if (!d) return <Empty testId="override-chart-loading">Working out your override by month…</Empty>;
  if (!d.total_gbp) {
    return (
      <Panel testId="override-chart-empty-panel">
        <Empty testId="override-chart-empty">
          No override earned yet. The moment a partner you brought in has a case fund, it lands here and the bars
          start building — month by month, partner by partner.
        </Empty>
      </Panel>
    );
  }

  return (
    <Panel
      testId="override-chart"
      title={`Override earned over ${d.window} months`}
      sub={d.note}
      right={
        <div style={{ display: "inline-flex", gap: 6 }}>
          {[6, 12, 24].map((n) => (
            <Btn key={n} sm kind={window_ === n ? "primary" : "ghost"} onClick={() => setWindow(n)}
                 testId={`override-window-${n}`}>{n}m</Btn>
          ))}
        </div>
      }
      footer={
        <>
          <span>
            <b style={{ color: "var(--ink-3)" }}>{gbp(d.total_gbp)}</b> across {d.deals} funded case{d.deals === 1 ? "" : "s"} ·{" "}
            {gbp(d.paid_gbp)} paid, {gbp(d.owed_gbp)} owed
            {d.best ? <> · best month <b style={{ color: "var(--ink-3)" }}>{d.best.long}</b> at {gbp(d.best.total_gbp)}</> : null}
          </span>
          <span style={{ color: "var(--mut)" }}>
            {d.months_earning} of {d.window} months earning · {gbp(d.average_gbp)} average
          </span>
        </>
      }
    >
      <Legend series={series} />
      <div style={{ width: "100%", height: 280 }} data-testid="override-chart-canvas">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 6, right: 4, left: -8, bottom: 0 }} barCategoryGap="22%">
            <CartesianGrid stroke="rgba(11,18,32,.07)" vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false}
                   tick={{ fontSize: 11.5, fill: "#64748B", fontWeight: 600 }} />
            <YAxis tickLine={false} axisLine={false} width={62}
                   tickFormatter={(v) => (v ? gbp(v, { compact: true }) : "0")}
                   tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <Tooltip cursor={{ fill: "rgba(0,85,255,.05)" }} content={<Hover series={series} />} />
            {series.map((s, i) => (
              <Bar key={s.key} dataKey={s.key} stackId="o" fill={s.colour}
                   radius={i === series.length - 1 ? [5, 5, 0, 0] : undefined}
                   animationDuration={700} animationBegin={i * 60} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      <Tb testId="override-chart-table" style={{ marginTop: 22 }} head={<>
        <Th style={{ width: "40%" }}>Partner</Th>
        <Th className="r" style={{ width: "15%" }}>Funded</Th>
        <Th className="r" style={{ width: "15%" }}>Best month</Th>
        <Th className="r" style={{ width: "15%" }}>Average</Th>
        <Th className="r" style={{ width: "15%" }}>Earned</Th>
      </>}>
        {(d.recruits || []).map((r, i) => {
          const months = (r.by_month || []).map((v, j) => ({ v, m: d.months[j] })).filter((x) => x.v > 0);
          const best = months.reduce((a, x) => (x.v > (a?.v || 0) ? x : a), null);
          return (
            <tr key={r.id} data-testid={`override-row-${r.id}`}>
              <Td className="lead">
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <i style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0,
                              background: i < TOP ? BANDS[i % BANDS.length] : BANDS[BANDS.length - 1] }} />
                  <div style={{ minWidth: 0 }}>
                    <CellMain>{r.name}</CellMain>
                    <CellSub>
                      {r.level === 2 ? `Tier 2 — via ${r.via || "a partner of yours"}` : "Tier 1 — recruited by you"}
                      {r.kind === "lender" ? " · lender" : ""}
                    </CellSub>
                  </div>
                </div>
              </Td>
              <Td className="r"><CellMain style={num}>{r.deals}</CellMain></Td>
              <Td className="r">
                <CellMain style={{ ...num, fontWeight: 600 }}>{best ? gbp(best.v) : "—"}</CellMain>
                {best ? <CellSub style={{ textAlign: "right" }}>{best.m.long}</CellSub> : null}
              </Td>
              <Td className="r">
                <CellMain style={{ ...num, color: "var(--mut)", fontWeight: 500 }}>
                  {months.length ? gbp(r.total_gbp / months.length) : "—"}
                </CellMain>
              </Td>
              <Td className="r"><CellMain style={{ ...num, fontWeight: 800 }}>{gbp(r.total_gbp)}</CellMain></Td>
            </tr>
          );
        })}
      </Tb>
    </Panel>
  );
}
